// #region Imports
import startCase from "lodash/startCase";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsIcon from "@mui/icons-material/Groups";
import IosShareIcon from "@mui/icons-material/IosShare";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import WarningIcon from "@mui/icons-material/Warning";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import { chain, uniqBy } from "lodash";
import {
  getTeamById,
} from "marketplace/teams/_store/marketplace-teams-redux-slice";
import {
  useGetTeamByIdQuery,
} from "marketplace/teams/_api/marketplace-team-api";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ChangeEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { MaskedContracts, MaskedTeam } from "work/_shared/masked-fields";
import {
  ActionsContainer,
  ButtonContainer,
  Content,
  ProposalTab,
  TabContent,
  TabsContainer,
} from "work/_views/work-page-styled-components";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  useCreateProposalMutation,
  useDeleteProposalMutation,
  useGetProposalByIdQuery,
  useGiveReviewerApprovalMutation,
  useSaveProposalMutation,
  useShareProposalMutation,
  useSubmitProposalMutation,
} from "work/proposals/_api/proposal-api";
import { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import {
  clearNewProposalBuilder,
  getOriginalProposalBuilderByProposalId,
  getProposalById,
  getUpdatedProposalBuilderByProposalId,
  isProposalLoadIssue,
  replaceOriginalProposalBuilder,
  replaceUpdatedProposalBuilder,
  resetBuilderByProposalId,
} from "work/proposals/_store/proposals-redux-slice";
import {
  CommentButtonLabel,
  CommentButton,
  Fieldset,
  HighlightCommentButton,
  ProposalActionButton,
  ProposalActions,
  ReviewerBadge,
  Header,
} from "work/proposals/_views/_shared/proposal-styled-components";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import ConflictsTab from "work/proposals/_views/drafts/tabs/draft-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/drafts/tabs/draft-proposal-details-tab";
import DiscountTab from "work/proposals/_views/drafts/tabs/draft-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/drafts/tabs/draft-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/drafts/tabs/draft-proposal-policies-tab";
import TeamTab from "work/proposals/_views/drafts/tabs/draft-proposal-team-tab";
import {
  clearUnsavedCommentsByProposalId,
  getArePendingComments,
  getAreUnsavedComments,
  publishComments,
  saveComments,
  updateAutoGeneratedComments,
  updateThreadSubscribers,
} from "work/proposals/comments/_store/comments-redux-slice";
import CommentsDrawer from "work/proposals/comments/_view/comments-drawer";
import AutoCommentGenerator from "work/proposals/comments/auto-comments/auto-comment-generator";
import {
  getCommentThreadsByProposal,
  getErrorLoadingCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import IssueTraverser from "work/proposals/issues/_views/proposal-issue-traverser";
import ProposalIssuesBadge from "work/proposals/issues/_views/proposal-issues-badge";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import ProposalField, {
  ProposalFieldCategory,
  ProposalFieldCategoryType,
  ProposalFieldName,
} from "work/proposals/proposal-fields/proposal-field";
import ReviewerSelector from "work/proposals/reviewers/_views/reviewer-selector";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import SelectedVendorTeamLeader from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team-leader/selected-vendor-team-leader";
import SelectedVendorTeam from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team/selected-vendor-team";
import WorkAgreement from "work/work-agreements/work-agreement";
import SubmitReviewButton from "work/proposals/_views/_buttons/submit-review-button";
// #endregion

// #region Styled Components
const PageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
}));
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  paddingRight: theme.spacing(3),
}));
const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
}));
const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(2),
}));
const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "showCommentDrawer",
})<{
  open?: boolean;
  showCommentDrawer?: boolean;
}>(({ theme, showCommentDrawer }) => ({
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
  display: "flex",
  height: "100%",
  flexDirection: "column",
  flex: 1,
  padding: theme.spacing(3),
  paddingRight: showCommentDrawer ? 0 : theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: showCommentDrawer ? "-400px" : 0,
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: "relative",
  width: "100%",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: 0,
      },
    },
  ],
}));
const ProposalActionSpan = styled("span")(({ theme }) => ({
  minWidth: theme.spacing(24),
  width: "100%",
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));
const NoBuilderLoader = styled(Loader)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  height: "100%",
  padding: theme.spacing(2),
  width: "100%",
}));
// #endregion

type DraftProposalAction = "edit" | "review" | "create" | "view";
const DraftProposalActions = new Set<DraftProposalAction>(["edit", "review", "create", "view"]);

export default function DraftProposalPage() {
  // #region Hooks
  const location = useLocation();
  const session = useSession();
  const navigate = useNavigate();
  const { action: actionParam, id: idParam, pageTab: pageTabParam } = useParams();
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const [searchParams] = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const pageAction: DraftProposalAction = (actionParam as DraftProposalAction) ?? "create";

  // Unsupported action
  if (!DraftProposalActions.has(pageAction)) {
    throw new Response("Not Found", { status: 404 });
  }
  // Proposal id is required for edit/review
  if (pageAction !== "create" && !idParam) {
    throw new Response("Not Found", { status: 404 });
  }

  // #region State
  const [activeReviewField, setActiveReviewField] = useState<ProposalField>();

  const [currentCommentField, setCurrentCommentField] = useState<ProposalField | undefined>();
  const [commentsPanelIsOpen, setCommentsPanelIsOpen] = useState<boolean>(false);
  const [commentsPanelTitle, setCommentsPanelTitle] = useState<string | undefined>();

  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [reviewerSelectorAnchor, setReviewerSelectorAnchor] = useState<HTMLButtonElement>();
  const [isManagingReviewers, setIsManagingReviewers] = useState<boolean>(false);
  const [isSelectingClient, setIsSelectingClient] = useState<boolean>(false);
  // #endregion

  // #region Globals
  const proposalId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const preSelectedLeaderInfo = SelectedVendorTeamLeader.fromURLSearchParams(searchParams);
  const preSelectedTeamInfo = SelectedVendorTeam.fromURLSearchParams(searchParams);
  const preSelectedMarketplaceTeam = useSelector(getTeamById(preSelectedTeamInfo?.targetTeamId));
  useGetTeamByIdQuery(
    preSelectedTeamInfo?.targetTeamId ? { teamId: preSelectedTeamInfo.targetTeamId.value } : skipToken
  );
  const existingWorkAgreement: WorkAgreement = location.state?.workAgreement;
  const disableEditing = pageAction === "view" || !pageAction;
  const activeTab = pageTabParam as PageTab;

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  // #region Proposal Queries
  const proposalJson = useSelector(getProposalById(proposalId));
  const proposal = useMemo(() => {
    if (!proposalJson || isProposalLoadIssue(proposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: proposalJson,
      currentUser: session.user,
    });
  }, [proposalJson, session.user]);

  const hasDocs =
    MaskedContracts.isPresentNotMasked(proposalJson?.conflicts) && (proposalJson.conflicts.documents?.length ?? 0) > 0;

  const proposalQuery = useGetProposalByIdQuery(
    !proposalId || proposalId.isEmpty || hasDocs
      ? skipToken
      : { proposalId: proposalId?.value ?? Guid.EMPTY.value, session },
  );

  // #region Proposal Builder Queries
  const originalBuilderJSON = useSelector(getOriginalProposalBuilderByProposalId(proposalId ?? Guid.EMPTY));
  const originalBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: originalBuilderJSON, session });
  }, [originalBuilderJSON, session]);

  const updatedBuilderJSON = useSelector(getUpdatedProposalBuilderByProposalId(proposalId ?? Guid.EMPTY));
  const updatedBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: updatedBuilderJSON, session });
  }, [updatedBuilderJSON, session]);

  const updatedBuilderIssues = useMemo(() => {
    return updatedBuilder ? ProposalIssues.fromBuilder(updatedBuilder) : new ProposalIssues();
  }, [updatedBuilder]);
  const updatedBuilderIssuesRef = useRef<ProposalIssues>(updatedBuilderIssues);
  useEffect(() => {
    updatedBuilderIssuesRef.current = updatedBuilderIssues;
  }, [updatedBuilderIssues]);

  // #region Comments Queries
  const commentThreads = useSelector(getCommentThreadsByProposal(proposal));
  const isLoadingCommentThreads = useSelector(getIsLoadingCommentThreadsByProposal(proposal));
  const wasErrorLoadingCommentThreads = useSelector(getErrorLoadingCommentThreadsByProposal(proposal));
  const isLoading = proposalQuery.isLoading || isLoadingCommentThreads;
  const comments = useSelector((state: RootState) => state.comments.entries);
  const arePendingComments = useSelector(getArePendingComments(proposal));
  const areUnsavedComments = useSelector(getAreUnsavedComments(proposal));

  const [saveProposalQuery] = useSaveProposalMutation();
  const [createProposalQuery] = useCreateProposalMutation();
  const [shareProposalQuery, shareProposalResult] = useShareProposalMutation();
  const [submitProposalQuery] = useSubmitProposalMutation();
  const [deleteProposalQuery] = useDeleteProposalMutation();
  const [giveReviewerApprovalQuery, giveReviewerApprovalQueryResult] = useGiveReviewerApprovalMutation();

  const isReviewing = useMemo(() => {
    return (
      [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])].some((reviewer) =>
        reviewer.userId.isEqualTo(session.user?.id),
      ) ?? false
    );
  }, [proposal, session.user]);
  const vendorReviewerCount = updatedBuilder?.currentSpec?.vendorReviewers?.length ?? 0;
  const clientReviewerCount = updatedBuilder?.currentSpec?.clientReviewers?.length ?? 0;
  const hasReviewers = session.context?.viewingAsVendor ? vendorReviewerCount > 0 : clientReviewerCount > 0;

  const generalFieldUnreadCount =
    commentThreads
      ?.filter((thread) => thread.field.isEqualTo(ProposalField.General))
      .reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0;

  const issues = useMemo(() => ProposalIssues.fromBuilder(updatedBuilder ?? undefined), [updatedBuilder]);
  const issueFields = useMemo(() => {
    return uniqBy(issues?.entries.map((issue) => issue.field) ?? [], (field) => field.toJSON());
  }, [issues]);

  const proposalRef = useRef(proposal);
  const updatedBuilderRef = useRef(updatedBuilder);
  const arePendingCommentsRef = useRef(arePendingComments);
  const areUnsavedCommentsRef = useRef(areUnsavedComments);

  // Gate for initializing the review field once data is ready
  const didInitReviewFieldRef = useRef(false);

  // #region Lifecycle
  useEffect(() => {
    // If editing a submitted proposal, reroute to revise flow
    if (pageAction === "edit" && proposal?.isSubmitted) {
      navigate(`/proposals/active/revise/${proposal.id?.value}/details`, { replace: true });
      return;
    }

    // Proposal invalid/not found/no longer reviewer
    const shouldShowNotFound =
      Boolean(proposalId) &&
      !proposalId?.isEmpty &&
      proposal === null &&
      pageAction !== "create" &&
      !proposalQuery.isLoading;

    if (shouldShowNotFound) {
      if (pageAction === "review" && idParam) {
        enqueueSnackbar("You are no longer a reviewer on this proposal", { variant: "warning" });
      } else if (idParam) {
        enqueueSnackbar("Proposal not found", { variant: "warning" });
      }
      navigate("/proposals/drafts", { replace: true });
    }
  }, [pageAction, activeTab, navigate, proposal, proposalId, proposalQuery.isLoading, idParam]);

  // Keep refs in sync
  useEffect(() => {
    proposalRef.current = proposal;
    updatedBuilderRef.current = updatedBuilder;
    arePendingCommentsRef.current = arePendingComments;
    areUnsavedCommentsRef.current = areUnsavedComments;
  }, [proposal, updatedBuilder, areUnsavedComments, arePendingComments, comments]);

  // Permissions bootstrap
  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  // #region Functions
  const handleOpenCommentsPanel = useCallback(
    (field?: ProposalField, title?: string) => {
      if (field) setCurrentCommentField(field);
      if (title) setCommentsPanelTitle(title);
      setCommentsPanelIsOpen(true);
    },
    [setCurrentCommentField, setCommentsPanelTitle, setCommentsPanelIsOpen],
  );

  const generateChangeAutoComments = useCallback(
    async (proposalBuilder: ProposalBuilder) => {
      if (!proposal?.id || proposal?.isArchived) return;
      const autoComments = new AutoCommentGenerator(
        session,
        proposal,
        proposalBuilder,
        commentThreads ?? [],
      ).generateAutoCommentsFromBuilder();
      dispatch(updateAutoGeneratedComments(autoComments));
    },
    [proposal, session, commentThreads, dispatch],
  );

  // Comment threads bootstrap
  useEffect(() => {
    if (
      !proposal ||
      proposal.supersededById ||
      isLoadingCommentThreads ||
      wasErrorLoadingCommentThreads ||
      commentThreads
    )
      return;
    dispatch(populateCommentThreadsByProposal({ proposal, session }));
  }, [proposal, isLoadingCommentThreads, wasErrorLoadingCommentThreads, commentThreads, dispatch, session]);

  // Builder initialization
  useEffect(() => {
    if (proposalId && !proposal) return; // still loading proposal
    if (updatedBuilderJSON || updatedBuilder) return;

    const initializedBuilder =
      originalBuilder ??
      new ProposalBuilder({
        session,
        startingSpec: proposal?.spec,
        existingWorkAgreement,
        preSelectedClientLeader:
          session.context?.viewingAsVendor && preSelectedLeaderInfo?.hasSigningAuthority
            ? preSelectedLeaderInfo?.toEntityMember()
            : undefined,
        preSelectedVendorLeader:
          !session.context?.viewingAsVendor && preSelectedLeaderInfo?.hasSigningAuthority
            ? preSelectedLeaderInfo?.toEntityMember()
            : undefined,
        preSelectedTeam: preSelectedMarketplaceTeam?.toWorkTeam(),
      });
    dispatch(
      replaceOriginalProposalBuilder({
        proposalId: proposalId ?? Guid.EMPTY,
        builder: initializedBuilder,
      }),
    );
    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: proposalId ?? Guid.EMPTY,
        builder: initializedBuilder,
      }),
    );
  }, [
    proposal,
    proposalId,
    updatedBuilder,
    updatedBuilderJSON,
    originalBuilder,
    preSelectedLeaderInfo,
    preSelectedMarketplaceTeam,
    session,
    session.context?.viewingAsVendor,
    existingWorkAgreement,
    dispatch,
  ]);

  // Auto comment generation
  useEffect(() => {
    if (!updatedBuilder || !proposalId) return;
    void generateChangeAutoComments(updatedBuilder);
  }, [generateChangeAutoComments, proposalId, proposalId?.value, updatedBuilder]);

  // Account view switching
  useEffect(() => {
    if (!session?.canSwitchContext || !proposal) return;

    // Don't switch account type if we're reviewing
    if (
      isReviewing && (
        !session.context?.viewingAsVendor &&
        proposal.clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(session.user?.id))
      ) || (
        session.context?.viewingAsVendor &&
        proposal.vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(session.user?.id))
      )
    ) return;

    // If viewing as vendor and are the client team leader, switch to client account
    if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      MaskedTeam.isPresentNotMasked(proposal.clientLeader) &&
      session.context?.viewingAsVendor &&
      proposal.clientLeader?.userId.isEqualTo(session.user?.id)
    ) {
      session.setAccountViewType(AccountType.Client);
    }

    // If viewing as client and are the vendor team leader, switch to vendor account
    if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      !session.context?.viewingAsVendor &&
      (
        proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        proposal.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id))
      )
    ) {
      session.setAccountViewType(AccountType.Vendor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal, proposal?.id?.value]);

  // Open comments panel when active review field becomes available
  useEffect(() => {
    if (!activeReviewField) return;
    if (proposal?.createdDate) {
      handleOpenCommentsPanel(activeReviewField);
    }
  }, [
    actionParam,
    activeReviewField,
    activeTab,
    handleOpenCommentsPanel,
    navigate,
    proposal?.createdDate,
    proposalId?.value,
  ]);

  // Initialize activeReviewField when data is ready
  useEffect(() => {
    if (didInitReviewFieldRef.current) return;
    if (!updatedBuilder) return;

    const proposalFieldCategories = Object.values(ProposalFieldCategory);
    const proposalFieldNames = Object.values(ProposalFieldName);

    const currentCategory = proposalFieldCategoryFromPageTab(activeTab);

    const tabIssues: ProposalField[] = issueFields.filter((field) => field.category === currentCategory) ?? [];

    const tabUnreadComments: ProposalField[] = uniqBy(
      commentThreads
        ?.filter(
          (thread) => thread.numUnreadMessages && thread.field.category === proposalFieldCategoryFromPageTab(activeTab),
        )
        .map((thread) => thread.field) ?? [],
      (field) => field.toJSON(),
    );

    const fieldsPendingReview: ProposalField[] = chain([...tabUnreadComments, ...tabIssues])
      .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ""}`)
      .sortBy([
        (field) => proposalFieldCategories.indexOf(field.category),
        (field) => proposalFieldNames.indexOf(field.name),
      ])
      .value();

    const next = fieldsPendingReview[0];
    setActiveReviewField(next ?? undefined);
    didInitReviewFieldRef.current = true;
  }, [activeTab, commentThreads, issueFields, updatedBuilder]);

  function handleTabChange(_event: ChangeEvent<object> | null, newTab: PageTab) {
    if (!proposalId) {
      navigate(`/proposals/drafts/create/${newTab}`);
      return;
    }
    navigate(`/proposals/drafts/${actionParam}/${proposalId.value}/${newTab}`);
  }

  const handleTraverseToNewField = useCallback(
    (field?: ProposalField) => {
      setActiveReviewField(field);

      if (!field) return;

      const targetTab = pageTabFromProposalFieldCategory(field.category);
      const currentTabCategory = proposalFieldCategoryFromPageTab(activeTab);

      if (field.category !== currentTabCategory) {
        const action = actionParam ?? "create";

        if (proposalId?.value) {
          navigate(`/proposals/drafts/${action}/${proposalId.value}/${targetTab}`, {
            replace: true,
          });
        } else {
          navigate(`/proposals/drafts/create/${targetTab}`, {
            replace: true,
          });
        }
      }

      if (proposal?.createdDate) {
        handleOpenCommentsPanel(field);
      }
    },
    [activeTab, actionParam, handleOpenCommentsPanel, navigate, proposal?.createdDate, proposalId?.value],
  );

  function handleManageReviewersClicked(event: MouseEvent<HTMLButtonElement>) {
    setReviewerSelectorAnchor(event.currentTarget);
    setIsManagingReviewers(true);
  }

  async function handleSubmitClicked() {
    if (!updatedBuilder) {
      throw new Error("Proposal cannot be submitted without a builder.");
    }

    let message: string;
    const clientReviewers = updatedBuilder?.currentSpec.clientReviewers ?? proposal?.clientReviewers ?? [];
    const vendorReviewers = updatedBuilder?.currentSpec.vendorReviewers ?? proposal?.vendorReviewers ?? [];
    if (
      (session.context?.viewingAsVendor &&
        vendorReviewers.length &&
        vendorReviewers.length !== vendorReviewers.filter((reviewer) => reviewer.dateApproved).length) ||
      (!session.context?.viewingAsVendor &&
        clientReviewers.length &&
        clientReviewers.length !== clientReviewers.filter((reviewer) => reviewer.dateApproved).length)
    ) {
      message = "Not all reviewers have reviewed the proposal. Do you still want to submit the proposal?";
    } else {
      message = "Submit the proposal now?";
    }

    const response = await confirm({
      title: "Submit Proposal",
      message: message,
      okButtonText: "Submit",
    });
    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsSaving(true);
      const draftProposalIdValue = await handleSaveAndShareChanges({
        confirmShare: false,
      });
      setIsSaving(false);
      if (!draftProposalIdValue) {
        throw new Error("Failed to save proposal before submission.");
      }
      setIsSubmitting(true);
      await submitProposalQuery({ proposalId: draftProposalIdValue }).unwrap();
      enqueueSnackbar("Proposal submitted successfully", {
        variant: "success",
      });

      if (draftProposalIdValue) {
        navigate(`/proposals/active/revise/${draftProposalIdValue}/details`, { replace: true });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to submit proposal", { variant: "error" });
    } finally {
      setIsSaving(false);
      setIsSubmitting(false);
    }
  }

  function shouldDisableTabs(): boolean {
    return isSaving || isManagingReviewers || isSelectingClient;
  }

  async function handleDeleteClicked() {
    if (proposal?.isArchived) return;
    if (!proposal?.id) {
      console.warn("Proposal not found");
      return;
    }

    try {
      const rejectResponse = await confirm({
        title: "Delete Draft Proposal?",
        message: "This cannot be undone.",
      });

      if (rejectResponse === ConfirmResponse.Cancel) return;

      setIsDeleting(true);
      await deleteProposalQuery({ proposalId: proposal.id.value }).unwrap();
      enqueueSnackbar("Proposal deleted", { variant: "info" });
      navigate("/proposals/drafts", { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete proposal", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleProposalBuilderUpdated(updatedProposalBuilder: ProposalBuilder | undefined) {
    if (!updatedProposalBuilder) {
      console.warn("handleProposalBuilderUpdated called with empty proposal builder");
      return;
    }
    startTransition(() => {
      dispatch(
        replaceUpdatedProposalBuilder({
          proposalId: proposal?.id ?? Guid.EMPTY,
          builder: updatedProposalBuilder,
        }),
      );
    });
  }

  const handleCloseCommentsPanel = () => {
    setCommentsPanelIsOpen(false);
  };

  async function handleFinalizeReviewClicked() {
    if (!proposal?.id) throw new Error("No proposal to finalize review for");

    try {
      await giveReviewerApprovalQuery({
        proposalId: proposal.id.value,
      }).unwrap();
      enqueueSnackbar("Proposal review finalized", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to finalize proposal review", {
        variant: "error",
      });
    }
  }

  const hasUserApprovedReview = useMemo(() => {
    const reviewers: ProposalReviewer[] = [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])];

    return reviewers.some((reviewer) => reviewer.userId.isEqualTo(session.user?.id) && reviewer.dateApproved);
  }, [proposal?.clientReviewers, proposal?.vendorReviewers, session.user?.id]);

  function getShareButtonLabel() {
    if (isSubmitting) return "Submitting...";
    if (isSaving) return "Saving...";
    if (!hasReviewers) return "No Reviewers";
    if (arePendingComments && !proposal?.isModifiedByBuilder(updatedBuilder)) {
      return "Share Comments";
    }
    if (proposal?.isModifiedByBuilder(updatedBuilder)) {
      return "Share Changes";
    }
    return "Nothing To Share";
  }

  function handleCommentsClicked(field: ProposalField, name?: string) {
    if (!proposal?.id) return;
    handleOpenCommentsPanel(field, startCase(name ?? field.name));
  }

  const handleSaveDraft = useCallback(async () => {
    if (!updatedBuilderRef.current) {
      throw new Error("Proposal builder is required to save a proposal.");
    }

    setIsSaving(true);

    try {
      let responseJSON: ProposalJSON;

      if (proposalId && proposalRef.current) {
        responseJSON = await saveProposalQuery({
          session,
          updatedProposal: proposalRef.current,
          proposalBuilderJSON: updatedBuilderRef.current.toJSON(),
        }).unwrap();
        dispatch(saveComments({ session }));
      } else {
        const unsavedDraftProposal = updatedBuilderRef.current.buildNewDraft();
        responseJSON = await createProposalQuery({
          proposal: unsavedDraftProposal,
          session,
        }).unwrap();
        dispatch(clearNewProposalBuilder());
      }

      if (!responseJSON.id) throw new Error("Saved proposal is missing an ID");
      enqueueSnackbar("Changes saved", { variant: "success" });

      if (responseJSON.id && !location.pathname.includes("/proposals/drafts/edit")) {
        navigate(`/proposals/drafts/edit/${responseJSON.id}/details`, { replace: true });
        didInitReviewFieldRef.current = false;
      }
    } catch (error) {
      console.error("Error saving proposal", error);
      enqueueSnackbar("Error saving proposal", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }, [createProposalQuery, dispatch, location.pathname, navigate, proposalId, saveProposalQuery, session]);

  const handleSaveAndShareChanges = useCallback(
    async (args?: { proposal?: Proposal; confirmShare?: boolean, commentsOnly?: boolean }): Promise<string | null | undefined> => {
      const { proposal, confirmShare = true, commentsOnly = false } = args ?? {};

      let proposalToSaveAndShare = proposal ?? proposalRef.current;

      if (!proposalToSaveAndShare) {
        proposalToSaveAndShare = updatedBuilderRef.current?.buildNewDraft();
      } else if (!proposalToSaveAndShare.isSubmitted && updatedBuilderRef.current) {
        proposalToSaveAndShare = updatedBuilderRef.current.updateDraftProposal(proposalToSaveAndShare);
      }

      if (!proposalToSaveAndShare) {
        console.error("No proposal to save and share");
        enqueueSnackbar("There was a problem saving and sharing the proposal", {
          variant: "error",
        });
        return;
      }

      const userCanShare = proposalToSaveAndShare.userCanShare(session.user);

      if (confirmShare) {
        const response = await confirm({
          title: "Share Changes?",
          message: `Are you sure you want to share pending ${commentsOnly ? "comments" : "comments and/or changes"} for review?`,
          okButtonText: "Share",
        });

        if (response === ConfirmResponse.Cancel) return;
      }

      if (arePendingCommentsRef.current) {
        await dispatch(publishComments({ session })).unwrap();
        enqueueSnackbar("Comments published", { variant: "info" });
        if (!userCanShare || commentsOnly) return;
      }

      if (!userCanShare) {
        enqueueSnackbar("You do not have permission to share this proposal", {
          variant: "warning",
        });
        return;
      }

      let savedProposalJSON: ProposalJSON | null = null;
      if (!proposalToSaveAndShare.id) {
        savedProposalJSON = await createProposalQuery({
          proposal: proposalToSaveAndShare,
          session,
        }).unwrap();
        dispatch(clearNewProposalBuilder());
      } else if (proposalToSaveAndShare.isModified) {
        savedProposalJSON = await shareProposalQuery({
          proposal: proposalToSaveAndShare,
          session,
        }).unwrap();
        if (savedProposalJSON) {
          enqueueSnackbar("Proposal changes shared internally", {
            variant: "success",
          });
        } else {
          console.error("Failed to share proposal changes", shareProposalResult.error);
          enqueueSnackbar("Failed to share proposal changes", {
            variant: "error",
          });
          return;
        }
      }

      if (proposalToSaveAndShare) {
        dispatch(updateThreadSubscribers({ proposal: proposalToSaveAndShare, session }));
      }

      return savedProposalJSON?.id ?? proposalToSaveAndShare.id?.value;
    },
    [session, confirm, dispatch, createProposalQuery, shareProposalQuery, shareProposalResult.error],
  );
  // #endregion

  // #region Render
  const pageTitle = useMemo(() => {
    if (pageAction === "view") return "View";
    if (pageAction === "create") return "Create";
    if (pageAction === "review") return "Review Draft";
    if (pageAction === "edit") return "Edit Draft";
    return "View";
  }, [pageAction]);
  const reviewers = useMemo(
    () =>
      (session.context?.viewingAsVendor
        ? updatedBuilder?.currentSpec.vendorReviewers
        : updatedBuilder?.currentSpec.clientReviewers) ?? [],
    [
      session.context?.viewingAsVendor,
      updatedBuilder?.currentSpec.vendorReviewers,
      updatedBuilder?.currentSpec.clientReviewers,
    ],
  );
  const totalApprovals = useMemo(() => reviewers.filter((reviewer) => reviewer.dateApproved).length, [reviewers]);
  const unreadCommentFields = useMemo(() => {
    return uniqBy(
      commentThreads?.filter((thread) => thread.numUnreadMessages).map((thread) => thread.field) ?? [],
      (field) => field.toJSON(),
    );
  }, [commentThreads]);
  const commentsToolTip = proposal?.id ? "Add comments" : "Save proposal to add comments";
  const manageReviewersDisabled =
    !updatedBuilder ||
    isSaving ||
    isSelectingClient ||
    isSelectingClient ||
    isManagingReviewers ||
    reviewerSelectorAnchor !== undefined ||
    !proposal?.id ||
    disableEditing ||
    isReviewing;
  const totalReviewers = reviewers.length ?? 0;
  const areChangesToShare = proposal?.isModifiedByBuilder(updatedBuilder) || arePendingComments;
  let isDirty = areUnsavedComments;
  if (!proposal?.isSubmitted) {
    isDirty = isDirty || Boolean(updatedBuilder?.isDifferentFrom(originalBuilder));
  }
  const disableSave = !isDirty || !updatedBuilderIssues.canSave;
  let saveButtonTooltip = "Save changes (just for yourself)";
  if (!isDirty) {
    saveButtonTooltip = "No changes to save";
  } else if (!updatedBuilderIssues.canSave) {
    saveButtonTooltip = "Fill out all required fields to save";
  }

  const handleNavigateBack = useCallback(async () => {
    if (isDirty) {
      const response = await confirm({
        title: "Discard changes?",
        message: "Are you sure you want to discard changes?",
        okButtonText: "Discard",
        alternativeOkText: disableSave ? undefined : "Save & Close",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;
      if (response === ConfirmResponse.AltOk) {
        await handleSaveDraft();
      }
    }

    if (proposalId && !proposalId.isEmpty) {
      dispatch(clearUnsavedCommentsByProposalId(proposalId));
      dispatch(
        resetBuilderByProposalId({
          id: proposalId,
          session,
        }),
      );
    } else {
      dispatch(clearNewProposalBuilder());
    }

    didInitReviewFieldRef.current = false;
    navigate("/proposals/drafts", { replace: true });
  }, [confirm, disableSave, dispatch, handleSaveDraft, isDirty, navigate, proposalId, session]);

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;

  return (
    <PageContainer>
      <Main open={commentsPanelIsOpen} showCommentDrawer={!!proposalId && !isLoading}>
        <HeaderContainer>
          <TitleContainer>
            <Chip
              icon={<ArrowBackIcon />}
              size="medium"
              label="Back"
              onClick={handleNavigateBack}
              color="primary"
              sx={{ mr: 2 }}
            />
            <Typography variant="h4">
              <span style={{ color: "gray", marginRight: "8px" }}>Proposals &gt;</span>
              {pageTitle}
            </Typography>
          </TitleContainer>
          <ActionButtonsContainer>
            <IssueTraverser
              issueFields={issueFields}
              unreadCommentFields={unreadCommentFields}
              currentField={activeReviewField}
              prevLabel="Prev Issue"
              nextLabel="Next Issue"
              onTraverseToNewField={handleTraverseToNewField}
            />
            <Tooltip title={saveButtonTooltip}>
              <span>
                <LoadingButton
                  variant="outlined"
                  size="large"
                  loading={isSaving}
                  color="primary"
                  disabled={isSaving || disableSave}
                  startIcon={<SaveIcon />}
                  onClick={() => handleSaveDraft()}
                >
                  Save
                </LoadingButton>
              </span>
            </Tooltip>
            <CommentButton>
              <Tooltip
                enterDelay={600}
                placement="bottom-end"
                title={proposal?.id ? "" : "Save proposal to enable commenting"}
              >
                <ButtonContainer>
                  <HighlightCommentButton
                    active={activeReviewField?.isEqualTo(ProposalField.General)}
                    size="medium"
                    color="primary"
                    disabled={!proposal?.id}
                    onClick={() => handleOpenCommentsPanel(ProposalField.General)}
                  >
                    <Badge
                      variant={generalFieldUnreadCount ? "standard" : "dot"}
                      color="secondary"
                      overlap="circular"
                      badgeContent={generalFieldUnreadCount > 0 ? generalFieldUnreadCount : undefined}
                      invisible={generalFieldUnreadCount === 0}
                    >
                      <CommentIcon fontSize="medium" />
                    </Badge>
                  </HighlightCommentButton>
                  <CommentButtonLabel variant="button" color={proposal?.id ? "primary" : "darkgray"}>
                    Comments
                  </CommentButtonLabel>
                </ButtonContainer>
              </Tooltip>
            </CommentButton>
          </ActionButtonsContainer>
        </HeaderContainer>
        {!updatedBuilder && <NoBuilderLoader />}
        {updatedBuilder && (
          <>
            <Header>
              <TabsContainer
                variant="scrollable"
                scrollButtons="auto"
                indicatorColor="primary"
                textColor="primary"
                value={activeTab}
                onChange={handleTabChange}
              >
                <ProposalTab
                  value={PageTab.Details}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      commentThreads={commentThreads ?? []}
                      category={ProposalFieldCategory.Details}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Team}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      commentThreads={commentThreads ?? []}
                      category={ProposalFieldCategory.Team}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.FeeSchedule}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      commentThreads={commentThreads ?? []}
                      category={ProposalFieldCategory.FeeSchedule}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Conflicts}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      commentThreads={commentThreads ?? []}
                      category={ProposalFieldCategory.Conflicts}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Policies}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      commentThreads={commentThreads ?? []}
                      category={ProposalFieldCategory.Policies}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Discount}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      commentThreads={commentThreads ?? []}
                      category={ProposalFieldCategory.Discount}
                    />
                  }
                />
              </TabsContainer>
            </Header>
            <ActionsContainer>
              <ProposalActions>
                {isReviewing && (
                  <Tooltip
                    enterDelay={600}
                    placement="top"
                    title={(
                      !proposal?.isModifiedByBuilder(updatedBuilder) &&
                      !areUnsavedComments)
                      ? "Finish reviewing proposal"
                      : "Finish reviewing proposal or share feedback"
                    }
                  >
                    <span>
                      <SubmitReviewButton
                        disabled={
                          isLoading ||
                          isSaving ||
                          giveReviewerApprovalQueryResult.isLoading ||
                          isSelectingClient ||
                          isManagingReviewers
                        }
                        finalized={hasUserApprovedReview}
                        loading={isSaving}
                        hasPendingChanges={proposal?.isModifiedByBuilder(updatedBuilder) ?? false}
                        hasPendingComments={areUnsavedComments}
                        onFinalize={handleFinalizeReviewClicked}
                        onSubmitChangesAndComments={() => handleSaveAndShareChanges({ confirmShare: true })}
                        onSubmitCommentsOnly={() => handleSaveAndShareChanges({
                          confirmShare: true,
                          commentsOnly: true
                        })}
                      />
                    </span>
                  </Tooltip>
                )}
                {!disableEditing && (
                  <>
                    {!isReviewing && (
                      <Fieldset>
                        <Tooltip
                          enterDelay={600}
                          placement="bottom"
                          title={proposal?.id ? "" : "Save proposal to add reviewers"}
                        >
                          <ProposalActionSpan>
                            <ProposalActionButton
                              variant="outlined"
                              color="primary"
                              startIcon={<GroupsIcon />}
                              endIcon={
                                <ReviewerBadge
                                  invisible={manageReviewersDisabled || !reviewers.length}
                                  badgeContent={
                                    <Tooltip
                                      title={
                                        totalApprovals === totalReviewers
                                          ? "All reviewers approved"
                                          : "Awaiting reviewer responses"
                                      }
                                    >
                                      <span>
                                        <sup style={{ verticalAlign: "0.2em" }}>{totalApprovals}</sup>
                                        <span style={{ verticalAlign: 0 }}>&frasl;</span>
                                        <sub style={{ verticalAlign: "-0.2em" }}>{totalReviewers}</sub>
                                      </span>
                                    </Tooltip>
                                  }
                                  color={totalReviewers === totalApprovals ? "success" : "warning"}
                                />
                              }
                              loading={isLoading}
                              disabled={isLoading || manageReviewersDisabled}
                              onClick={handleManageReviewersClicked}
                            >
                              Reviewers
                            </ProposalActionButton>
                          </ProposalActionSpan>
                        </Tooltip>
                        <ReviewerSelector
                          proposal={proposal ?? undefined}
                          popoverAnchor={reviewerSelectorAnchor}
                          onPopoverClose={() => {
                            setReviewerSelectorAnchor(undefined);
                            setIsManagingReviewers(false);
                          }}
                          onReviewersChanged={(updatedReviewers) => {
                            if (!session.context) throw new Error("Session not properly registered.");
                            if (!updatedBuilder)
                              throw new Error("Proposal builder not registered properly in store.");
                            void handleProposalBuilderUpdated(
                              session.context?.viewingAsVendor
                                ? updatedBuilder?.setVendorReviewers(updatedReviewers)
                                : updatedBuilder?.setClientReviewers(updatedReviewers),
                            );
                          }}
                          onSend={() => proposal && handleSaveAndShareChanges({ confirmShare: true })}
                        />
                        <Tooltip
                          enterDelay={600}
                          placement="bottom"
                          title={
                            areChangesToShare
                              ? "Share changes and/or comments for review"
                              : "No reviewers or no unshared changes/comments"
                          }
                        >
                          <span>
                            <ProposalActionButton
                              variant="outlined"
                              color="primary"
                              startIcon={<IosShareIcon />}
                              loading={isSaving}
                              disabled={
                                isLoading ||
                                !areChangesToShare ||
                                isSaving ||
                                giveReviewerApprovalQueryResult.isLoading ||
                                isSelectingClient ||
                                isManagingReviewers
                              }
                              onClick={() => proposal && handleSaveAndShareChanges({ confirmShare: true })}
                            >
                              {getShareButtonLabel()}
                            </ProposalActionButton>
                          </span>
                        </Tooltip>
                      </Fieldset>
                    )}
                    {!disableEditing && !isReviewing && (
                      <Tooltip
                        enterDelay={600}
                        placement="bottom"
                        title={
                          issues?.canSubmit ? "Submit proposal to other party" : "Fill out required fields to submit"
                        }
                      >
                        <ProposalActionSpan>
                          <ProposalActionButton
                            variant="outlined"
                            color="success"
                            startIcon={issues?.canSubmit ? <SendIcon /> : <WarningIcon />}
                            loading={isSubmitting || isPending}
                            disabled={
                              isLoading ||
                              isSaving ||
                              isSelectingClient ||
                              isManagingReviewers ||
                              !issues?.canSubmit ||
                              disableEditing ||
                              isReviewing
                            }
                            onClick={handleSubmitClicked}
                          >
                            Submit
                          </ProposalActionButton>
                        </ProposalActionSpan>
                      </Tooltip>
                    )}
                    {proposal?.creator?.userId.isEqualTo(session.user?.id) && (
                      <ProposalActionButton
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        loading={isDeleting}
                        disabled={
                          isLoading ||
                          isSelectingClient ||
                          isDeleting ||
                          isSubmitting ||
                          isSaving ||
                          isManagingReviewers ||
                          !proposal?.id ||
                          isReviewing
                        }
                        onClick={handleDeleteClicked}
                      >
                        Delete Proposal
                      </ProposalActionButton>
                    )}
                  </>
                )}
              </ProposalActions>
            </ActionsContainer>
            <Content>
              <TabContent>
                {updatedBuilder && (
                  <>
                    {activeTab === PageTab.Details && (
                      <DetailsTab
                        commentThreads={commentThreads ?? []}
                        isSaved={Boolean(proposal?.id)}
                        issues={issues}
                        activeReviewField={activeReviewField}
                        proposalBuilder={updatedBuilder}
                        disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                        onSelectingClientChange={(isSelecting: boolean) => setIsSelectingClient(isSelecting)}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onCommentsClicked={handleCommentsClicked}
                      />
                    )}
                    {activeTab === PageTab.Team && (
                      <TeamTab
                        proposalBuilder={updatedBuilder}
                        isSaved={Boolean(proposal?.id)}
                        issues={issues}
                        disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                        activeReviewField={activeReviewField}
                        commentThreads={commentThreads ?? []}
                        commentsToolTip={commentsToolTip}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onCommentsClicked={handleCommentsClicked}
                      />
                    )}
                    {activeTab === PageTab.FeeSchedule && (
                      <FeeScheduleTab
                        proposalBuilder={updatedBuilder}
                        isSaved={Boolean(proposal?.id)}
                        issues={issues}
                        commentThreads={commentThreads}
                        commentsToolTip={commentsToolTip}
                        disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onCommentsClicked={handleCommentsClicked}
                      />
                    )}
                    {activeTab === PageTab.Conflicts && (
                      <ConflictsTab
                        proposalBuilder={updatedBuilder}
                        isSaved={Boolean(proposal?.id)}
                        issues={issues}
                        commentThreads={commentThreads}
                        commentsToolTip={commentsToolTip}
                        disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onCommentsClicked={handleCommentsClicked}
                      />
                    )}
                    {activeTab === PageTab.Policies && (
                      <PoliciesTab
                        proposalBuilder={updatedBuilder}
                        isSaved={Boolean(proposal?.id)}
                        issues={issues}
                        commentThreads={commentThreads}
                        disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                        activeReviewField={activeReviewField}
                        commentsToolTip={commentsToolTip}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onCommentsClicked={handleCommentsClicked}
                      />
                    )}
                    {activeTab === PageTab.Discount && (
                      <DiscountTab
                        proposalBuilder={updatedBuilder}
                        isSaved={Boolean(proposal?.id)}
                        issues={issues}
                        disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                        activeReviewField={activeReviewField}
                        commentThreads={commentThreads ?? []}
                        commentsToolTip={commentsToolTip}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onCommentsClicked={handleCommentsClicked}
                      />
                    )}
                  </>
                )}
              </TabContent>
            </Content>
          </>
        )}
      </Main>
      {proposal?.createdDate && (
        <CommentsDrawer
          proposal={proposal}
          commentThreads={commentThreads}
          currentCommentField={currentCommentField}
          activeReviewField={activeReviewField}
          isOpen={commentsPanelIsOpen}
          isSaving={isSaving}
          title={commentsPanelTitle}
          onOpen={handleOpenCommentsPanel}
          onClose={handleCloseCommentsPanel}
        />
      )}
    </PageContainer>
  );
}

function proposalFieldCategoryFromPageTab(pageTab: PageTab): ProposalFieldCategoryType {
  switch (pageTab) {
    case PageTab.Details:
      return ProposalFieldCategory.Details;
    case PageTab.Team:
      return ProposalFieldCategory.Team;
    case PageTab.FeeSchedule:
      return ProposalFieldCategory.FeeSchedule;
    case PageTab.Conflicts:
      return ProposalFieldCategory.Conflicts;
    case PageTab.Policies:
      return ProposalFieldCategory.Policies;
    case PageTab.Discount:
      return ProposalFieldCategory.Discount;
  }
}

function pageTabFromProposalFieldCategory(proposalFieldCategory: ProposalFieldCategoryType): PageTab {
  switch (proposalFieldCategory) {
    case ProposalFieldCategory.Details:
      return PageTab.Details;
    case ProposalFieldCategory.Team:
      return PageTab.Team;
    case ProposalFieldCategory.FeeSchedule:
      return PageTab.FeeSchedule;
    case ProposalFieldCategory.Conflicts:
      return PageTab.Conflicts;
    case ProposalFieldCategory.Policies:
      return PageTab.Policies;
    case ProposalFieldCategory.Discount:
      return PageTab.Discount;
  }
}
